import { Button, Fade, Skeleton, TextField } from '@mui/material';
import {
  FieldTemplate,
  FieldType,
  OptionSourceType,
  SelectFieldDisplayComponent,
} from 'api/models/FieldTemplate';
import { useParams } from 'react-router-dom';
import {
  NumberField,
  SelectField,
  StringField,
  DateField,
} from 'routes/FormTemplateEditor/FormEditor/FieldTemplate/Fields';
import RadioField from 'routes/FormTemplateEditor/FormEditor/FieldTemplate/Fields/RadioField';
import { GeneralIds, ModelTypes } from 'types/types';
import usePatientFieldsMap from 'hooks/usePatientFieldsMap';
import useRequest from 'hooks/useRequest';
import { getFieldTemplateByAlias } from 'api';
import { Box } from '@mui/system';
import { useTranslation } from 'react-i18next';
import useQuery from 'hooks/useQuery';
import StringListField from 'components/StringListField/StringListField';
import OptionAutocompleteField from 'routes/FormTemplateEditor/FormEditor/FieldTemplate/Fields/OptionAutocompleteField';
import { useMemo } from 'react';

interface ConditionFieldRendererProps {
  alias?: string;
  patientFieldAttribute?: string;
  value?: string | number | string[] | undefined | null;
  onChange: (value: string | number | string[] | undefined | null) => void;
  disabled?: boolean;
}
export default function ConditionFieldRenderer({
  alias,
  patientFieldAttribute,
  value,
  onChange: passedOnChange,
  disabled,
}: ConditionFieldRendererProps) {
  const { t } = useTranslation();
  const patientFieldMap = usePatientFieldsMap();
  const { projectId } = useParams<GeneralIds>();

  const { data: fieldByAlias, loading } = useRequest<FieldTemplate>(
    getFieldTemplateByAlias,
    {
      dependencies: [projectId, alias, patientFieldAttribute],
    },
    [{ projectId, alias }],
  );

  const patientField = patientFieldMap.get(patientFieldAttribute ?? '');
  const field = patientField ?? fieldByAlias;

  // this call is needed to fetch the refered field for select fields
  const { data: selectFields } = useQuery<FieldTemplate[]>({
    type: ModelTypes.fieldTemplate,
    ids: {
      projectId,
      id: field?.fieldAttributes?.optionsSourceFieldTemplateId,
    },
    skip: !field?.fieldAttributes?.optionsSourceFieldTemplateId,
  });

  const isCodeLibrarySource = useMemo(
    () => field?.fieldAttributes?.optionsSourceType === OptionSourceType.CodeLibrary,
    [field]);

  if (loading) {
    return (
      <Fade in={loading}>
        <Skeleton
          variant="rectangular"
          height="100%"
          sx={{ borderRadius: '10px' }}
          animation="wave"
        />
      </Fade>
    );
  }

  const onChange = (e: any) => {
    passedOnChange(e.target.value);
  };

  switch (field?.fieldType) {
    case FieldType.Number:
      return (
        <NumberField
          disabled={disabled}
          field={field}
          value={value ?? null}
          onChange={onChange}
        />
      );
    case FieldType.Text:
    case FieldType.Calculated:
      return (
        <StringField
          disabled={disabled}
          field={field}
          value={value ?? undefined}
          onChange={onChange}
        />
      );
    case FieldType.Select:
      const castedValue = value as string | string[] | null;
      return (
        <Box>
          {isCodeLibrarySource ? (
            <OptionAutocompleteField
              onChange={(value: any) => {
                passedOnChange(value)
              }}
              value={(castedValue ?? null)}
              optionsSourceCodeLibraryId={
                field.fieldAttributes.optionsSourceCodeLibraryId
              }
              label={field.fieldAttributes.label}
              defaultValue={(field.fieldAttributes.defaultValue ?? null) as string | string[] | null}
              isMultiSelect={true}
              description={field.fieldAttributes.description}
            />
          ) : (
            <>
              {field.fieldAttributes.component ===
                SelectFieldDisplayComponent.Dropdown ? (
                <SelectField
                  disabled={disabled}
                  field={{
                    ...field,
                    fieldAttributes: {
                      ...field.fieldAttributes,
                      isMultiSelect: true,
                    },
                  }}
                  value={value ?? null}
                  name={field.id}
                  onChange={onChange}
                  onClear={() => passedOnChange(undefined)}
                />
              ) : (
                <RadioField
                  disabled={disabled}
                  field={{
                    ...field,
                    fieldAttributes: {
                      ...field.fieldAttributes,
                      isMultiSelect: true,
                    },
                  }}
                  value={value ?? null}
                  name={field.id}
                  onChange={onChange}
                />
              )}
              <Button
                disabled={disabled}
                onClick={() => {
                  if (selectFields?.[0] && !field.fieldAttributes?.options) {
                    passedOnChange(
                      selectFields[0].fieldAttributes?.options?.map(
                        option => option.value,
                      ),
                    );
                  } else {
                    passedOnChange(
                      field.fieldAttributes?.options?.map(option => option.value),
                    );
                  }
                }}
              >
                {t('dialogs.addField.selectAll')}
              </Button>
            </>
          )}
        </Box>
      );
    case FieldType.StringList:
      return (
        <StringListField
          separator=","
          disabled={disabled}
          fullWidth
          value={value}
          onChange={onChange}
        />
      );
    case FieldType.Date:
      return (
        <DateField
          disabled={disabled}
          field={field}
          value={value ?? null}
          onChange={onChange}
        />
      );
    default:
      return (
        <TextField
          disabled={disabled}
          onChange={onChange}
          value={value}
          fullWidth
        />
      );
  }
}
